// از جوابِ `admin_app_activity` تا چیزی که می‌شود وب را در آن از موبایل جدا
// دید (WHOSUP-242).
//
// عمداً در core و نه در SQL — همان تقسیمِ کاری که `journeyReport.ts` و
// `sourceTest.ts` دارند: دیتابیس **می‌شمارد**، این‌جا تصمیم گرفته می‌شود که
// چه چیزی «یک‌طرفه» است و ردیف‌ها با چه ترتیبی نشان داده شوند. عوض کردنِ
// این تصمیم‌ها نباید مایگریشن بخواهد، و `npm test` با اعدادِ ساختگی
// می‌آزمایدشان — بدونِ دیتابیس، بدونِ مرورگر.
import type {
  AppActivity, Counts, FunnelData, PeopleMix, RetentionData, SurfaceCounts, SurfaceKey,
} from '../data/opsRepo.ts';
import { FUNNEL_EVENTS, type FunnelName } from './funnel.ts';

/** «همه» یعنی وب و موبایل روی هم — پیش‌فرضِ صفحه. */
export type SurfaceFilter = 'all' | SurfaceKey;

export const SURFACES: readonly SurfaceKey[] = ['web', 'mobile'];

export const isSurfaceFilter = (v: string): v is SurfaceFilter =>
  v === 'all' || v === 'web' || v === 'mobile';

/**
 * یک رویداد، همان‌طور که دیده می‌شود: عددِ پنجره‌ی انتخاب‌شده، و شکستش.
 *
 * `side` جوابِ همان سوالِ تیکت است: این رویداد فقط روی سایت ثبت می‌شود،
 * فقط روی اپ، یا هر دو.
 */
export interface EventRow {
  name: string;
  /** عددِ سطحِ انتخاب‌شده (یا جمعِ هر دو، وقتی فیلتر «همه» است). */
  counts: Counts;
  sessions: number;
  web: number;
  mobile: number;
  side: 'web' | 'mobile' | 'both';
}

/** یک روز، با دو سریِ روی هم. */
export interface DayRow {
  day: string;
  web: number;
  mobile: number;
  /** جمعِ دو سری — چون خودِ `n` می‌تواند از ردیف‌های بی‌سطحِ قدیمی هم بیاید. */
  total: number;
  sessionsWeb: number;
  sessionsMobile: number;
}

/* ─────────────────────────── کارت‌های بالا ─────────────────────────── */

const ZERO: Counts = { total: 0, d10: 0, run: 0 };

const EMPTY_SURFACE: SurfaceCounts = {
  total: ZERO, sessions: ZERO, people: ZERO, guests: ZERO,
};

/**
 * چهار عددِ بالای صفحه برای فیلترِ انتخاب‌شده.
 *
 * ⚠ روی دیتابیسی که هنوز مایگریشنِ 0148 را ندارد `surfaces` نیست. آن‌وقت
 * فیلترِ سطح صفر نشان می‌دهد و نه عددِ کل — رابط جای خالی را می‌گوید.
 * برگرداندنِ عددِ «همه» به‌جای عددِ «وب» یعنی یک دروغِ بی‌صدا.
 */
export function cardsFor(a: AppActivity, filter: SurfaceFilter): SurfaceCounts {
  if (filter === 'all') {
    return {
      total: a.total, sessions: a.sessions, people: a.people, guests: a.guests,
    };
  }
  return a.surfaces?.[filter] ?? EMPTY_SURFACE;
}

/** همان، برای هر دو سطح کنارِ هم — جدولِ «وب در برابر موبایل». */
export const surfaceCards = (a: AppActivity): { surface: SurfaceKey; v: SurfaceCounts }[] =>
  SURFACES.map((s) => ({ surface: s, v: a.surfaces?.[s] ?? EMPTY_SURFACE }));

export const peopleMix = (a: AppActivity, window: 'total' | 'd10'): PeopleMix =>
  a.people_mix?.[window] ?? { web_only: 0, mobile_only: 0, both: 0 };

/**
 * سهمِ هر سطح از کلِ رویدادهای ده روز — بینِ ۰ و ۱.
 *
 * مخرج جمعِ **دو سطح** است و نه `total.d10`: ردیف‌های قدیمی‌ای که سطحشان
 * چیزِ دیگری باشد نباید سهم را بی‌صدا کم کنند تا جمعِ درصدها ۱۰۰ نشود.
 */
export function surfaceShare(a: AppActivity): Record<SurfaceKey, number> {
  const w = a.surfaces?.web?.total?.d10 ?? 0;
  const m = a.surfaces?.mobile?.total?.d10 ?? 0;
  const sum = w + m;
  return sum ? { web: w / sum, mobile: m / sum } : { web: 0, mobile: 0 };
}

/* ─────────────────────────── رویدادها ─────────────────────────── */

/**
 * ⚠ «هیچ‌کدام» هم `both` است و نه یک‌طرفه: ردیفی که در این پنجره هیچ
 * شمارشِ سطحی ندارد (ردیفِ قدیمی، یا سطحی که دیگر فرستاده نمی‌شود) حرفی
 * درباره‌ی نبودنِ یک پلتفرم نمی‌زند. گذاشتنش در فهرستِ «فقط یک‌جا» یعنی
 * یک زنگِ خطرِ الکی.
 */
const sideOf = (web: number, mobile: number): EventRow['side'] => {
  if (web > 0 && mobile > 0) return 'both';
  if (web > 0) return 'web';
  if (mobile > 0) return 'mobile';
  return 'both';
};

/**
 * جدولِ «پرتکرارترین رویدادها» برای فیلترِ انتخاب‌شده.
 *
 * ⚠ زیرِ فیلتر، ردیفِ صفر **حذف** می‌شود و نه اینکه صفر نشان داده شود:
 * فهرستی که نصفش صفر باشد خوانده نمی‌شود، و همان ردیف‌ها یک کارتِ جدا
 * دارند («چه چیزی فقط یک‌جا ثبت می‌شود») که دقیقاً درباره‌شان است.
 */
export function eventRows(a: AppActivity, filter: SurfaceFilter): EventRow[] {
  const rows = (a.by_name ?? []).map((r) => {
    const web = r.n_web ?? 0;
    const mobile = r.n_mobile ?? 0;
    const counts: Counts = filter === 'all'
      ? { d10: r.n, run: r.n_run }
      : filter === 'web'
        ? { d10: web, run: r.run_web ?? 0 }
        : { d10: mobile, run: r.run_mobile ?? 0 };
    const sessions = filter === 'all'
      ? r.sessions
      : (filter === 'web' ? (r.s_web ?? 0) : (r.s_mobile ?? 0));
    return { name: r.name, counts, sessions, web, mobile, side: sideOf(web, mobile) };
  });
  return rows
    .filter((r) => filter === 'all' || r.counts.d10 > 0)
    .sort((x, y) => y.counts.d10 - x.counts.d10);
}

/**
 * رویدادهایی که فقط یک سطح ثبتشان می‌کند.
 *
 * ═══ چرا این فهرست ارزشش را دارد ═══
 *
 * دو چیزِ کاملاً متفاوت به این‌جا می‌رسند و صفحه باید هر دو را نشان بدهد،
 * چون از روی عدد نمی‌شود فرقشان را گفت:
 *
 *   • رویدادی که آن‌طرف **معنی ندارد** — چیزی که فقط مرورگر دارد.
 *   • رویدادی که آن‌طرف هم باید ثبت می‌شد و **یادمان رفته**. این یکی یک
 *     کارِ ناتمام است و تا وقتی کسی نشانش ندهد، به شکلِ «کاربرهای موبایل
 *     این کار را نمی‌کنند» خوانده می‌شود.
 *
 * پس این‌جا فقط واقعیت شمرده می‌شود و قضاوت نمی‌شود؛ کدامش کدام است را
 * آدم تصمیم می‌گیرد.
 *
 * ⚠ ملاک کلِ پنجره‌ی ده‌روزه است و نه فیلترِ فعلی: «فقط روی وب دیده شده»
 * حرفی درباره‌ی هر دو سطح است، پس با فیلترِ موبایل هم همان فهرست است.
 */
export const oneSidedEvents = (a: AppActivity): EventRow[] =>
  eventRows(a, 'all').filter((r) => r.side !== 'both');

/* ─────────────────────── تعادلِ شروع و پایانِ نشست ─────────────────────── */

/**
 * نسبتِ `session_end` به `session_start` بیرون از این بازه یعنی شمارشِ
 * نشست‌ها خودش خراب است (WHOSUP-296) — مثلِ ۱۲۹ پایان در برابرِ ۵۹ شروع
 * روی وب در هفته‌ی منتهی به ۶ اکتبر ۲۰۲۶، که هیچ‌جای صفحه نشانش نداد.
 */
export const SESSION_RATIO_MIN = 0.8;
export const SESSION_RATIO_MAX = 1.2;

export interface SessionBalance {
  surface: SurfaceKey;
  start: number;
  end: number;
  /** `null` وقتی شروعی نیست و نسبت تعریف نمی‌شود. */
  ratio: number | null;
  /** `unknown` یعنی هیچ‌کدام ثبت نشده — حکمی نیست، نه «سالم». */
  verdict: 'ok' | 'off' | 'unknown';
}

/**
 * برای هر سطح، شروع و پایانِ نشست در پنجره‌ی ده‌روزه‌ی صفحه — از همان
 * `by_name`، پس عددِ تازه‌ای از SQL لازم نیست.
 *
 * ⚠ پایان بی‌هیچ شروعی «خراب» است و نه «نامعلوم»: دقیقاً همان خرابی‌ای است
 * که این هشدار برایش ساخته شده.
 */
export function sessionBalance(a: AppActivity): SessionBalance[] {
  const row = (name: string) => (a.by_name ?? []).find((r) => r.name === name);
  const start = row('session_start');
  const end = row('session_end');
  return SURFACES.map((surface) => {
    const s = (surface === 'web' ? start?.n_web : start?.n_mobile) ?? 0;
    const e = (surface === 'web' ? end?.n_web : end?.n_mobile) ?? 0;
    if (s === 0 && e === 0) return { surface, start: 0, end: 0, ratio: null, verdict: 'unknown' };
    if (s === 0) return { surface, start: 0, end: e, ratio: null, verdict: 'off' };
    const ratio = e / s;
    const ok = ratio >= SESSION_RATIO_MIN && ratio <= SESSION_RATIO_MAX;
    return { surface, start: s, end: e, ratio, verdict: ok ? 'ok' : 'off' };
  });
}

/* ─────────────────────────── قیف ─────────────────────────── */

/** نامِ فارسیِ پنج قدمِ قیف (WHOSUP-291)، به همان ترتیبِ `FUNNEL_EVENTS`. */
export const FUNNEL_FA: Record<FunnelName, string> = {
  link_entry: 'ورود از لینک',
  event_open: 'باز کردنِ رویداد',
  rsvp: 'پاسخ / علاقه',
  share: 'اشتراکِ لینک',
  invite_sent: 'دعوت',
};

/** یک قدم، برای فیلترِ انتخاب‌شده — از جوابِ `admin_funnel` (WHOSUP-292). */
export interface FunnelStep {
  name: FunnelName;
  /** آدم‌های یکتا (کاربرِ واردشده یا نشستِ مهمان) در بازه. */
  people: number;
  web: number;
  mobile: number;
  /**
   * `false` یعنی «هنوز ثبت نمی‌شود»: صفر آدم. صفر را «۰٪» نشان دادن یعنی
   * ادعای اینکه همه رها کردند، وقتی شاید فقط نسخه‌ی فرستنده نرسیده.
   */
  recorded: boolean;
  /** پهنای میله: نسبت به پرجمعیت‌ترین قدم، بینِ ۰ و ۱. */
  width: number;
  /** نسبت به قدمِ قبلی؛ `null` برای قدمِ اول یا وقتی این یا قبلی ثبت نشده. */
  ofPrev: number | null;
  verdict: FunnelVerdict | null;
}

export type FunnelVerdict = 'ok' | 'weak' | 'leak';

/** مرزِ حکم‌ها روی «نسبت به قبلی». */
export const FUNNEL_LEAK_BELOW = 0.1;
export const FUNNEL_WEAK_BELOW = 0.3;

export const FUNNEL_VERDICT_FA: Record<FunnelVerdict, string> = {
  ok: 'خوب', weak: 'ضعیف', leak: 'ریزشِ شدید',
};

export const funnelVerdict = (rate: number | null): FunnelVerdict | null =>
  rate === null ? null : rate < FUNNEL_LEAK_BELOW ? 'leak' : rate < FUNNEL_WEAK_BELOW ? 'weak' : 'ok';

/**
 * پنج قدم، همیشه هر پنج و به ترتیبِ `FUNNEL_EVENTS` — نامی که SQL برگرداند
 * ولی در فهرست نیست نادیده گرفته می‌شود.
 *
 * ⚠ این قیفِ «سخت» نیست: کسی که از فید آمده هم رویداد باز می‌کند، پس قدمِ
 * دوم می‌تواند از اول بزرگ‌تر باشد و «نسبت به قبلی» از ۱۰۰٪ بگذرد. برای
 * همین پهنا نسبت به بزرگ‌ترین قدم است، نه قدمِ اول.
 */
export function funnelSteps(f: FunnelData | null | undefined, filter: SurfaceFilter): FunnelStep[] {
  const base = FUNNEL_EVENTS.map((name) => {
    const raw = (f?.steps ?? []).find((x) => x.name === name);
    const web = raw?.web ?? 0;
    const mobile = raw?.mobile ?? 0;
    const people = filter === 'all' ? (raw?.total ?? 0) : (filter === 'web' ? web : mobile);
    return { name, people, web, mobile, recorded: people > 0 };
  });
  const peak = Math.max(1, ...base.map((s) => s.people));
  return base.map((s, i) => {
    const prev = i > 0 ? base[i - 1] : null;
    const ofPrev = prev && prev.recorded && s.recorded ? s.people / prev.people : null;
    return { ...s, width: s.people / peak, ofPrev, verdict: funnelVerdict(ofPrev) };
  });
}

/** نامِ فارسیِ ویژگی‌هایی که «بیشتر» نشان می‌دهد. */
export const FUNNEL_KEY_FA: Record<string, string> = {
  source: 'منبع', from: 'صفحه‌ی ورود', kind: 'نوعِ لینک', response: 'پاسخ',
  method: 'روش', link: 'لینک', target: 'دعوت از',
};

export interface FunnelDetail { key: string; value: string; people: number }

/**
 * جزئیاتِ یک قدم برای فیلتر: هر (ویژگی، مقدار) یک ردیف، پرجمعیت اول.
 *
 * ⚠ زیرِ «همه»، عددِ وب و موبایل **جمع** می‌شود — آدمی که روی هر دو بوده
 * دو بار؛ رابط همین را زیرِ جدول می‌گوید.
 */
export function funnelDetails(f: FunnelData | null | undefined, name: FunnelName, filter: SurfaceFilter): FunnelDetail[] {
  const m = new Map<string, FunnelDetail>();
  for (const d of f?.details ?? []) {
    if (d.name !== name || (filter !== 'all' && d.surface !== filter)) continue;
    const k = `${d.key} ${d.value}`;
    const cur = m.get(k) ?? { key: d.key, value: d.value, people: 0 };
    cur.people += d.people;
    m.set(k, cur);
  }
  return [...m.values()].sort((x, y) => x.key.localeCompare(y.key) || y.people - x.people);
}

/* ─────────────────────────── برگشت ─────────────────────────── */

/**
 * یک نرخِ برگشت. `early` یعنی «هنوز زود است»: کسی از آن هفته هست که
 * پنجره‌اش تمام نشده، پس صفر یا هر درصدی گفتن ادعایی است که هنوز داده‌اش
 * نرسیده (WHOSUP-310، قیدِ ۳).
 */
export type RetentionRate =
  | { kind: 'early' }
  | { kind: 'none' }
  | { kind: 'rate'; back: number; of: number; rate: number };

export interface RetentionRow {
  /** دوشنبه‌ی هفته، `YYYY-MM-DD`. */
  week: string;
  newcomers: number;
  /** روزِ بعد: ۲۴ تا ۴۸ ساعت بعد از اولین رویداد. */
  nextDay: RetentionRate;
  /** هفته‌ی بعد: ۲ تا ۸ روز بعد. */
  nextWeek: RetentionRate;
}

const rateOf = (n: number, ready: number, back: number): RetentionRate => {
  if (n === 0) return { kind: 'none' };
  if (ready < n) return { kind: 'early' };
  return { kind: 'rate', back, of: n, rate: back / n };
};

/**
 * ردیف‌های کارتِ «برگشت» برای فیلتر، تازه‌ترین هفته اول.
 *
 * ⚠ «همه» ردیف‌های `all` است و نه جمعِ وب و موبایل: کسی که اول روی سایت و
 * بعد روی اپ آمده در «همه» یک تازه‌وارد است که برگشته، ولی جمعِ دو سطح او را
 * دو تازه‌واردِ برنگشته می‌شمرد.
 */
export function retentionRows(r: RetentionData | null | undefined, filter: SurfaceFilter): RetentionRow[] {
  return (r?.cohorts ?? [])
    .filter((c) => c.surface === filter)
    .map((c) => ({
      week: c.week,
      newcomers: c.new,
      nextDay: rateOf(c.new, c.d1_ready, c.d1_back),
      nextWeek: rateOf(c.new, c.w_ready, c.w_back),
    }))
    .sort((x, y) => y.week.localeCompare(x.week));
}

export interface LostScreen {
  /** `null` یعنی هیچ صفحه‌ای برایش ثبت نشده. */
  screen: string | null;
  people: number;
}

/**
 * «بیشتر»: آخرین صفحه‌ای که برنگشته‌ها دیدند، پرتعدادترین اول. بی `week`،
 * همه‌ی هفته‌ها روی هم.
 */
export function lostScreens(
  r: RetentionData | null | undefined, filter: SurfaceFilter, week?: string,
): LostScreen[] {
  const m = new Map<string, number>();
  for (const l of r?.lost ?? []) {
    if (l.surface !== filter || (week !== undefined && l.week !== week)) continue;
    m.set(l.screen, (m.get(l.screen) ?? 0) + l.people);
  }
  return [...m.entries()]
    .map(([screen, people]) => ({ screen: screen || null, people }))
    .sort((x, y) => y.people - x.people || (x.screen ?? '').localeCompare(y.screen ?? ''));
}

/* ─────────────────────────── روزها ─────────────────────────── */

export const dayRows = (a: AppActivity): DayRow[] =>
  (a.by_day ?? []).map((d) => ({
    day: d.day,
    web: d.n_web ?? 0,
    mobile: d.n_mobile ?? 0,
    total: (d.n_web ?? 0) + (d.n_mobile ?? 0),
    sessionsWeb: d.s_web ?? 0,
    sessionsMobile: d.s_mobile ?? 0,
  }));

/** بلندترین ستونِ نمودار — مبنای درصدِ عرض/ارتفاعِ بقیه. هیچ‌وقت صفر نیست. */
export const dayPeak = (rows: DayRow[], filter: SurfaceFilter): number => Math.max(
  1,
  ...rows.map((r) => (filter === 'all' ? r.total : (filter === 'web' ? r.web : r.mobile))),
);
